import { useState, useCallback, useEffect } from 'react';
import { adminApi } from '../../utils/api';
import { Button, Card, Badge, Spinner, Alert } from './shared';

const STRATEGIES = [
    { value: 'notify', label: 'Notify', desc: 'Show message when user opens hub' },
    { value: 'agent_turn', label: 'Agent Turn', desc: 'Trigger agent to process and respond' },
];

const EVENT_TYPES = [
    'notification', 'price_alert', 'market_update', 'portfolio_alert',
    'news_alert', 'system_message', 'custom',
];

function HubSessionInfo({ session }) {
    if (!session) return null;
    return (
        <Card>
            <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-gray-900">Hub Session</h3>
                <Badge variant="green">{session.session_type}</Badge>
            </div>
            <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                    <span className="text-gray-500">Session ID</span>
                    <span className="font-mono text-xs text-gray-700">{session.session_id}</span>
                </div>
                <div className="flex justify-between">
                    <span className="text-gray-500">Name</span>
                    <span className="text-gray-700">{session.session_name}</span>
                </div>
                <div className="flex justify-between">
                    <span className="text-gray-500">Created</span>
                    <span className="text-gray-700">{new Date(session.created_at).toLocaleString()}</span>
                </div>
            </div>
        </Card>
    );
}

function EventHistory({ events }) {
    if (events.length === 0) return null;
    return (
        <Card>
            <h3 className="text-sm font-semibold text-gray-900 mb-3">Recent Events</h3>
            <div className="space-y-2 max-h-80 overflow-y-auto">
                {events.map((evt, i) => (
                    <div key={i} className="flex items-start gap-3 p-2 bg-gray-50 rounded-lg text-sm">
                        <Badge variant={evt.success ? 'green' : 'red'}>
                            {evt.success ? 'OK' : 'ERR'}
                        </Badge>
                        <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                                <span className="font-medium text-gray-900">{evt.strategy}</span>
                                <span className="text-gray-400">→</span>
                                <span className="text-gray-600">{evt.user_id}</span>
                            </div>
                            <p className="text-gray-500 truncate">{evt.content}</p>
                            <span className="text-xs text-gray-400">{evt.time}</span>
                        </div>
                    </div>
                ))}
            </div>
        </Card>
    );
}

export default function AdminHub() {
    const [userId, setUserId] = useState('');
    const [session, setSession] = useState(null);
    const [loading, setLoading] = useState(false);
    const [sending, setSending] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [events, setEvents] = useState([]);

    const [form, setForm] = useState({
        content: '',
        strategy: 'notify',
        source: 'admin',
        event_type: 'notification',
        system_prompt: '',
        agent_id: '',
    });
    const [agents, setAgents] = useState([]);

    useEffect(() => {
        adminApi.listAgents().then(data => {
            setAgents(data?.agents || data || []);
        }).catch(() => {});
    }, []);

    const lookupSession = useCallback(async () => {
        if (!userId.trim()) return;
        setLoading(true);
        setError(null);
        try {
            const hub = await adminApi.getHubSession(userId.trim());
            setSession(hub);
        } catch (e) {
            setError(e.message || 'Failed to lookup hub session');
            setSession(null);
        } finally {
            setLoading(false);
        }
    }, [userId]);

    const pushEvent = useCallback(async () => {
        if (!userId.trim() || !form.content.trim()) return;
        setSending(true);
        setError(null);
        setSuccess(null);
        const now = new Date().toLocaleTimeString();
        try {
            const payload = {
                user_id: userId.trim(),
                content: form.content.trim(),
                strategy: form.strategy,
                source: form.source || 'admin',
                event_type: form.event_type,
                system_prompt: form.system_prompt?.trim() || undefined,
            };
            if (form.agent_id) {
                payload.metadata = { agent_id: form.agent_id };
            }
            await adminApi.pushHubEvent(payload);
            setSuccess('Event pushed successfully');
            setEvents(prev => [{
                user_id: userId.trim(),
                content: form.content.trim(),
                strategy: form.strategy,
                success: true,
                time: now,
            }, ...prev].slice(0, 50));
            setForm(f => ({ ...f, content: '' }));
        } catch (e) {
            setError(e.message || 'Failed to push event');
            setEvents(prev => [{
                user_id: userId.trim(),
                content: form.content.trim(),
                strategy: form.strategy,
                success: false,
                time: now,
            }, ...prev].slice(0, 50));
        } finally {
            setSending(false);
            setTimeout(() => setSuccess(null), 3000);
        }
    }, [userId, form]);

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">Hub Management</h1>
                    <p className="text-sm text-gray-500 mt-1">Push messages to user hub sessions</p>
                </div>
            </div>

            {error && <Alert variant="error" onDismiss={() => setError(null)}>{error}</Alert>}
            {success && <Alert variant="success" onDismiss={() => setSuccess(null)}>{success}</Alert>}

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2 space-y-4">
                    <Card>
                        <h3 className="text-sm font-semibold text-gray-900 mb-4">Push Event</h3>

                        <div className="space-y-4">
                            <div className="flex gap-2">
                                <input
                                    type="text"
                                    placeholder="User ID"
                                    value={userId}
                                    onChange={e => setUserId(e.target.value)}
                                    onKeyDown={e => e.key === 'Enter' && lookupSession()}
                                    className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                />
                                <Button onClick={lookupSession} disabled={loading || !userId.trim()}>
                                    {loading ? <Spinner size="sm" /> : 'Lookup'}
                                </Button>
                            </div>

                            <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">Strategy</label>
                                <div className="grid grid-cols-3 gap-2">
                                    {STRATEGIES.map(s => (
                                        <button
                                            key={s.value}
                                            onClick={() => setForm(f => ({ ...f, strategy: s.value }))}
                                            className={`p-3 rounded-lg border text-left transition-colors ${
                                                form.strategy === s.value
                                                    ? 'border-blue-500 bg-blue-50'
                                                    : 'border-gray-200 hover:border-gray-300'
                                            }`}
                                        >
                                            <span className="text-sm font-medium block">{s.label}</span>
                                            <span className="text-xs text-gray-500">{s.desc}</span>
                                        </button>
                                    ))}
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="block text-xs font-medium text-gray-600 mb-1">Event Type</label>
                                    <select
                                        value={form.event_type}
                                        onChange={e => setForm(f => ({ ...f, event_type: e.target.value }))}
                                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg"
                                    >
                                        {EVENT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-gray-600 mb-1">Source</label>
                                    <input
                                        type="text"
                                        value={form.source}
                                        onChange={e => setForm(f => ({ ...f, source: e.target.value }))}
                                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg"
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">Agent (optional — used for agent_turn and summary)</label>
                                <select
                                    value={form.agent_id}
                                    onChange={e => setForm(f => ({ ...f, agent_id: e.target.value }))}
                                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg"
                                >
                                    <option value="">Default Agent</option>
                                    {agents.map(a => (
                                        <option key={a.agent_id || a.id} value={a.agent_id || a.id}>
                                            {a.display_name || a.name || a.agent_id || a.id}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">Content (visible to user)</label>
                                <textarea
                                    rows={4}
                                    placeholder="Message content to push to user's hub..."
                                    value={form.content}
                                    onChange={e => setForm(f => ({ ...f, content: e.target.value }))}
                                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                                />
                            </div>

                            <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">
                                    System Prompt (hidden — guides agent behavior)
                                </label>
                                <textarea
                                    rows={3}
                                    placeholder="e.g. Khi user hỏi về HPG, hãy phân tích kỹ thuật và đề xuất mức giá mục tiêu..."
                                    value={form.system_prompt}
                                    onChange={e => setForm(f => ({ ...f, system_prompt: e.target.value }))}
                                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent resize-none bg-amber-50"
                                />
                            </div>

                            <div className="flex justify-end">
                                <Button
                                    onClick={pushEvent}
                                    disabled={sending || !userId.trim() || !form.content.trim()}
                                    variant="primary"
                                >
                                    {sending ? <><Spinner size="sm" /> Sending...</> : 'Push Event'}
                                </Button>
                            </div>
                        </div>
                    </Card>

                    <EventHistory events={events} />
                </div>

                <div className="space-y-4">
                    <HubSessionInfo session={session} />

                    <Card>
                        <h3 className="text-sm font-semibold text-gray-900 mb-3">Quick Reference</h3>
                        <div className="space-y-3 text-xs text-gray-600">
                            <div>
                                <span className="font-medium text-gray-800 block">Silent Insert</span>
                                Message appears in hub history. Agent does not respond. Good for notifications.
                            </div>
                            <div>
                                <span className="font-medium text-gray-800 block">Stream Response</span>
                                Message queued for agent. Agent will process and respond when user opens hub.
                            </div>
                            <div>
                                <span className="font-medium text-gray-800 block">System Preload</span>
                                Hidden context. Not visible in history but agent can reference it.
                            </div>
                        </div>
                    </Card>

                    <Card>
                        <h3 className="text-sm font-semibold text-gray-900 mb-3">Redis Push</h3>
                        <div className="bg-gray-900 text-green-400 p-3 rounded-lg text-xs font-mono overflow-x-auto">
                            <pre>{`redis-cli PUBLISH hub:events '${JSON.stringify({
    user_id: userId || '<user_id>',
    content: 'Your message',
    strategy: 'silent_insert',
}, null, 2)}'`}</pre>
                        </div>
                    </Card>
                </div>
            </div>
        </div>
    );
}
